import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useChatClient } from './useChatClient';
import { ChatSidebar } from './ChatSidebar';
import { ChatMessageArea } from './ChatMessageArea';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

export const ChatClient = ({ isStandalone = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const { selectedConversation, markAllAsRead } = useChatClient();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showMessages, setShowMessages] = useState(false);

  // On mobile: show messages when conversation is selected
  // Reset to sidebar when conversation is deselected
  useEffect(() => {
    if (isMobile) {
      setShowMessages(!!selectedConversation);
    }
  }, [selectedConversation, isMobile]);

  // Mark messages as read when returning to chat page with an already selected conversation
  // Using a ref to track if this is the initial mount
  const initialMountRef = useRef(true);
  useEffect(() => {
    if (initialMountRef.current && selectedConversation?.id) {
      markAllAsRead(selectedConversation.id);
    }
    initialMountRef.current = false;
  }, [selectedConversation?.id, markAllAsRead]);

  const handleConversationSelect = () => {
    if (isMobile) {
      setShowMessages(true);
    }
  };

  const handleBack = () => {
    setShowMessages(false);
  };

  // Mobile view: show either sidebar OR messages (simplified logic)
  if (isMobile) {
    const showMessageArea = showMessages && selectedConversation;
    return (
      <Box
        className="flex w-full h-full overflow-hidden rounded-lg backdrop-blur-lg"
        sx={{ backgroundColor: colors.glass }}
      >
        <Box className="flex flex-col w-full h-full">
          {showMessageArea ? (
            <ChatMessageArea onBack={handleBack} />
          ) : (
            <ChatSidebar onConversationSelect={handleConversationSelect} isStandalone={isStandalone} />
          )}
        </Box>
      </Box>
    );
  }

  // Desktop view: show sidebar + messages side by side
  return (
    <Box
      className="flex w-full h-full overflow-hidden rounded-lg backdrop-blur-lg"
      sx={{ backgroundColor: colors.glass }}
    >
      {/* Sidebar - always visible on desktop */}
      <Box
        className="flex flex-col"
        sx={{
          width: selectedConversation ? 320 : '100%',
          maxWidth: 320,
          flexShrink: 0,
        }}
      >
        <ChatSidebar onConversationSelect={handleConversationSelect} isStandalone={isStandalone} />
      </Box>

      {/* Message Area */}
      {selectedConversation ? (
        <Box
          className="flex flex-col flex-1 min-w-0"
          sx={{ borderLeft: `1px solid ${colors.card}` }}
        >
          <ChatMessageArea onBack={handleBack} />
        </Box>
      ) : (
        <Box
          className="flex flex-col flex-1 items-center justify-center"
          sx={{ borderLeft: `1px solid ${colors.card}` }}
        >
          <Box className="text-center p-8">
            <ChatBubbleOutlineIcon
              sx={{
                fontSize: 64,
                color: colors.text,
                opacity: 0.2,
                mb: 2,
              }}
            />
            <Typography variant="body2" sx={{ color: colors.text, opacity: 0.4 }}>
              {t('Select a conversation or start a new chat')}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
